// "Share" modal for a group — web port of the legacy portal's Home Assistant
// modal (module/home/views/dashboard/home_assistant.php). Two things, both
// copy-to-clipboard:
//   1. the public JSON link  — https://api.divako.io/share/<code>, the group's
//      latest readings in JSON, and
//   2. the Home Assistant configuration.yaml snippet — served by the same
//      share service at /share/HA/config/<code> (fetched here client-side;
//      platform/api answers with Access-Control-Allow-Origin: *).
// <code> = base64({"g": gid, "c": share_code}); share_code arrives on the
// group via /me (app_category_group.share_code). Groups only — categories
// have no share code, exactly like the old portal.
//
// A group only ever got a share_code when someone opened its share tab in the
// `application` admin, so most groups reach this modal with none. Rather than
// the dead end the old portal showed ("Something went wrong!"), opening the
// modal on a codeless group generates one behind a loader and then shows the
// ordinary dialog. The same call backs the "Generate new token" button the
// admin has (project/views/category/tab/groups/show/share.php).

import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateGroupShareCode, listGroupAccess, type CategoryOrGroup } from '../lib/api';
import GroupAccessPanel from './GroupAccessPanel';
import { useGroupName } from '../lib/group';
import { wordingFor } from '../lib/projectType';
import { useUser } from '../lib/user';
import Icon from './Icon';
import { Modal, Spinner, toast } from './ui';

// The public share API. One host for every environment — same hardcode as
// the legacy portal (this is public, unauthenticated data).
const SHARE_BASE = 'https://api.divako.io/share';

function copyText(text: string, doneMessage: string): void {
  navigator.clipboard
    .writeText(text)
    .then(() => toast(doneMessage))
    .catch(() => {
      /* clipboard denied — the fields are selectable by hand */
    });
}

export default function GroupShareModal({
  group,
  onClose,
}: {
  group: CategoryOrGroup;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { reload } = useUser();
  const groupName = useGroupName();

  const isGroup = group.type === 'g';
  const wording = wordingFor(group.project_type);
  const [shareCode, setShareCode] = useState<string | null>(group.share_code ?? null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // The Access tab is offered only to admins. A viewer never reaches this
  // dialog (no Share button), and if one did they would see exactly the dialog
  // that existed before this feature, with no tab strip at all.
  const [tab, setTab] = useState<'links' | 'access'>('links');
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    if (!isGroup) return;
    let alive = true;
    listGroupAccess(group.id)
      .then((r) => {
        if (alive) setCanManage(r.can_manage);
      })
      .catch(() => {
        /* no Access tab; the Links half of the dialog still works */
      });
    return () => {
      alive = false;
    };
  }, [isGroup, group.id]);

  const code = shareCode ? btoa(JSON.stringify({ g: group.id, c: shareCode })) : null;
  const jsonLink = code ? `${SHARE_BASE}/${code}` : null;

  // First code being generated: the dialog is a bare loader until it lands,
  // then it is the same dialog a group with a code has always shown.
  const loadingFirstCode = busy && !jsonLink;

  const generate = useCallback(
    (announce: boolean) => {
      setConfirming(false);
      setFailed(false);
      setBusy(true);
      generateGroupShareCode(group.id)
        .then((s) => {
          setShareCode(s.share_code);
          // Refresh /me so the rest of the app — and a second visit to this
          // modal — sees the new code instead of asking for another one.
          reload();
          if (announce) toast(t('share.tokenGenerated'));
        })
        .catch(() => setFailed(true))
        .finally(() => setBusy(false));
    },
    [group.id, reload, t],
  );

  // Give a codeless group its first code on open. The endpoint always mints, so
  // this fires exactly once per group — never on a group that already has one.
  const requested = useRef<string | null>(null);
  useEffect(() => {
    if (!isGroup || group.share_code || requested.current === group.id) return;
    requested.current = group.id;
    generate(false);
  }, [isGroup, group.id, group.share_code, generate]);

  const [haConf, setHaConf] = useState<string | null>(null);
  const [haError, setHaError] = useState(false);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    setHaConf(null);
    setHaError(false);
    fetch(`${SHARE_BASE}/HA/config/${code}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        // The share service answers 200 {"error":"Unknown link"} for bad
        // codes — treat that as a failure, not a config.
        if (text.trim().startsWith('{"error"')) throw new Error('unknown link');
        if (alive) setHaConf(text.trim());
      })
      .catch(() => {
        if (alive) setHaError(true);
      });
    return () => {
      alive = false;
    };
  }, [code]);

  return (
    <Modal
      title={`${canManage ? t('share.titleWithAccess') : t('share.title')} · ${groupName(group)}`}
      onClose={onClose}
      wide
    >
      {canManage && (
        <div className="modal-tabs" role="tablist" aria-label={t('share.titleWithAccess')}>
          {(['links', 'access'] as const).map((id) => (
            <button
              key={id}
              role="tab"
              id={`share-tab-${id}`}
              aria-controls={`share-panel-${id}`}
              aria-selected={tab === id}
              tabIndex={tab === id ? 0 : -1}
              className="modal-tab"
              onClick={() => setTab(id)}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                e.preventDefault();
                const next = id === 'links' ? 'access' : 'links';
                setTab(next);
                document.getElementById(`share-tab-${next}`)?.focus();
              }}
            >
              {t(id === 'links' ? 'share.tabLinks' : 'share.tabAccess')}
            </button>
          ))}
        </div>
      )}

      {tab === 'access' && canManage && (
        <div role="tabpanel" id="share-panel-access" aria-labelledby="share-tab-access">
          <GroupAccessPanel gid={group.id} wording={wording} />
        </div>
      )}

      {/* Links panel — every child below is exactly what this dialog showed
          before tabs existed. `hidden` rather than unmounting, so switching
          tabs never refetches the Home Assistant config or re-triggers the
          codeless-group auto-mint. */}
      <div
        role={canManage ? 'tabpanel' : undefined}
        id="share-panel-links"
        aria-labelledby={canManage ? 'share-tab-links' : undefined}
        hidden={tab !== 'links'}
      >
        {!isGroup && <p className="share-note">{t('share.categoryUnsupported', { context: wording })}</p>}

        {isGroup && loadingFirstCode && (
          <div className="share-loading">
            <Spinner />
          </div>
        )}
        {isGroup && failed && <p className="share-note">{t('share.generateFailed', { context: wording })}</p>}

        {jsonLink && (
          <>
            <div className="share-section">
              <div className="share-label">
                <Icon name="copy" size={15} />
                {t('share.jsonLink')}
                <span className="share-help">{t('share.jsonLinkHelp')}</span>
              </div>
              <div className="input-row">
                <input type="text" readOnly value={jsonLink} onFocus={(e) => e.target.select()} />
                <button
                  className="icon-btn"
                  data-tip={t('share.copyLink')}
                  aria-label={t('share.copyLink')}
                  onClick={() => copyText(jsonLink, t('share.linkCopied'))}
                >
                  <Icon name="copy" size={17} />
                </button>
              </div>
            </div>

            <div className="share-section">
              <div className="share-label">
                <Icon name="home" size={15} />
                {t('share.haTitle')}
                <span className="share-help">{t('share.haHelp')}</span>
              </div>
              {haConf === null && !haError && <Spinner />}
              {haError && <p className="share-note">{t('share.loadFailed')}</p>}
              {haConf !== null && (
                <>
                  <pre className="share-pre">
                    <code>{haConf}</code>
                  </pre>
                  <button className="btn" onClick={() => copyText(haConf, t('share.configCopied'))}>
                    <Icon name="copy" size={16} />
                    {t('share.copyConfig')}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {isGroup && !loadingFirstCode && (
          <div className="share-section">
            <div className="share-label">
              <Icon name="refresh" size={15} />
              {t('share.tokenTitle')}
              {!confirming && <span className="share-help">{t('share.tokenHelp')}</span>}
            </div>
            {confirming ? (
              <div className="share-confirm">
                <p className="share-note">{t('share.tokenConfirm')}</p>
                <div className="share-confirm-actions">
                  <button className="btn btn-danger" onClick={() => generate(true)}>
                    {t('share.tokenConfirmYes')}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setConfirming(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                <Icon name="refresh" size={16} />
                {t('share.tokenGenerate')}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
